import { FormikProps, useFormik } from 'formik'
import React, { ReactNode, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'
import { TextInput } from '../../inputs/textInput'

import AddressInput from '../../inputs/addressInput'

import { ISchema } from 'yup'
import { TabNav } from '../../nav/tabNav'

type LocationData = object & {
  name: string
  city: string
  state: string
  long: string
  lat: string
  address: string
  placeId: string
  [key: string]: any
}

export const AddLocationModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (data: LocationData, reset: () => void) => void
  title: string
  header?: ReactNode
  showLocationPicker?: boolean
  fields: ('name' | 'address' | 'city' | 'state' | 'coordinates')[]
  additionalFields?: object
  schema?: ISchema<object>
  loading: boolean
  editOnSelect?: boolean
  placeholderPrefix?: string
  children?: (data: { formik: FormikProps<any> }) => ReactNode
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  title,
  header,
  showLocationPicker = true,
  fields,
  additionalFields = {},
  schema,
  loading,
  editOnSelect = true,
  placeholderPrefix = 'Location',
  children,
}) => {
  const [activeTab, setActiveTab] = useState('Search')
  const [step, setStep] = useState<number>(1)

  const formikData = useFormik({
    initialValues: {
      name: '',
      city: '',
      state: '',
      long: '',
      lat: '',
      address: '',
      placeId: '',
      ...additionalFields,
    },
    enableReinitialize: true,
    validationSchema:
      showLocationPicker && activeTab === 'Search' && step === 1
        ? undefined
        : schema,
    onSubmit: (vals, { resetForm }) => {
      if (
        showLocationPicker &&
        editOnSelect &&
        step === 1 &&
        activeTab === 'Search'
      ) {
        setStep(2)
        setFieldValue('name', '')
      } else {
        onSuccessCallback(
          {
            ...vals,
          },
          () => {
            resetForm()
            setStep(1)
          },
        )
      }
    },
  })

  const {
    values,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    isValid,
    resetForm,
    setValues,
    setFieldValue,
  } = formikData

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
        setStep(1)
      }}
      title={title}
      childrenClassName="md:!max-w-[420px]"
    >
      {header}
      {showLocationPicker && (
        <TabNav
          activeTab={activeTab}
          setActiveTab={(tab) => {
            resetForm({
              values: {
                ...formikData.initialValues,
                ...additionalFields,
              },
            })
            setActiveTab(tab)
            setStep(1)
          }}
          tabs={[
            { name: 'Location search', id: 'Search' },
            { name: 'Manual', id: 'Manual' },
          ]}
          className="mb-10"
        />
      )}

      {showLocationPicker && activeTab === 'Search' && step === 1 && (
        <>
          <AddressInput
            onSelect={async (selected) => {
              await setValues(
                {
                  ...values,
                  name: selected.address,
                  city: selected.city,
                  state: selected.state,
                  long: String(selected.lng),
                  lat: String(selected.lat),
                  address: selected.address,
                  placeId: selected.destinationGooglePlaceID,
                },
                true,
              )
            }}
            className=""
          />

          <Button
            label="Proceed"
            type="button"
            className="mt-20 w-full"
            disabled={loading}
            loading={loading}
            primary
            onClick={() => {
              handleSubmit()
            }}
          />
        </>
      )}

      {(!showLocationPicker ||
        activeTab === 'Manual' ||
        (activeTab === 'Search' && step === 2)) && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {fields.includes('name') && (
            <TextInput
              type="text"
              name="name"
              placeholder={`${placeholderPrefix} name`}
              value={values.name}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.name && touched.name ? errors.name : ''}
              hasIcon={false}
              className="!border-divider-gray"
              // title="Location Name"
            />
          )}

          {fields.includes('address') && (
            <TextInput
              type="text"
              name="address"
              placeholder={`${placeholderPrefix} address`}
              value={values.address}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.address && touched.address ? errors.address : ''}
              hasIcon={false}
              className="!border-divider-gray"
              // title="Address"
              readOnly={activeTab === 'Search'}
            />
          )}

          {fields.includes('city') && (
            <TextInput
              type="text"
              name="city"
              placeholder="City"
              value={values.city}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.city && touched.city ? errors.city : ''}
              hasIcon={false}
              className="!border-divider-gray"
              // title="City"
              readOnly={activeTab === 'Search'}
            />
          )}

          {fields.includes('state') && (
            <TextInput
              type="text"
              name="state"
              placeholder="State"
              value={values.state}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.state && touched.state ? errors.state : ''}
              hasIcon={false}
              className="!border-divider-gray"
              // title="State"
              readOnly={activeTab === 'Search'}
            />
          )}

          {fields.includes('coordinates') && (
            <div className="grid grid-cols-2 gap-4">
              <TextInput
                type="text"
                name="long"
                placeholder="Longitude"
                value={values.long}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={errors.long && touched.long ? errors.long : ''}
                hasIcon={false}
                className="!border-divider-gray"
                // title="Longitude"
                readOnly={activeTab === 'Search'}
              />

              <TextInput
                type="text"
                name="lat"
                placeholder="Latitude"
                value={values.lat}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={errors.lat && touched.lat ? errors.lat : ''}
                hasIcon={false}
                className="!border-divider-gray"
                // title="Latitude"
                readOnly={activeTab === 'Search'}
              />
            </div>
          )}

          {children ? children({ formik: formikData }) : undefined}

          <Button
            label="Proceed"
            type="submit"
            className="mt-10 w-full"
            disabled={loading || !isValid}
            loading={loading}
            primary
          />
        </form>
      )}
    </ModalComponent>
  )
}
